<template>
  <div class="farm-center-form">
    <form @submit.prevent="handleSubmit" class="form-fields">
      <AeTabPanel :tabs="fcTabs" v-model="activeTab" @click.stop @mousedown.stop>
        <!-- TAB 1: Anagrafica -->
        <template #anagrafica>
          
          <div class="tab-content">
            <!-- Nome + CUAA -->
            <div class="form-grid">
              <AeInput
                v-model="formData.name"
                :label="t('farmCenterForm.nameLabel')"
                :placeholder="t('farmCenterForm.namePlaceholder')"
                required
                :error="errors.name"
              />
              <AeInput
                v-model="formData.cuaa"
                :label="t('farmCenterForm.cuaaLabel')"
                :placeholder="t('farmCenterForm.cuaaPlaceholder')"
              />
            </div>

            <!-- Indirizzo/Luogo + Geocodifica (full width) -->
            <div class="location-group">
              <AeInput
                v-model="formData.place"
                :label="t('farmCenterForm.placeLabel')"
                :placeholder="t('farmCenterForm.placePlaceholder')"
                required
                :error="errors.place"
              />
              <AeButton
                variant="secondary"
                small
                type="button"
                iconOnly
                :disabled="!formData.place || geocoding"
                @click="handleGeocode"
                class="geocode-btn"
              >
                <AeIcon name="mapPin" :size="16" />
                <span> {{ geocoding ? t('farmCenterForm.searching') : t('farmCenterForm.geocode') }}</span>
              </AeButton>
              <AeButton
                variant="secondary"
                small
                type="button"
                iconOnly
                :disabled="gpsLoading"
                :title="gpsError || t('farmCenterForm.gpsPositionTitle')"
                @click="handleGps"
                class="geocode-btn"
              >
                <AeIcon name="crosshair" :size="16" />
                <span>{{ gpsLoading ? t('farmCenterForm.gpsSearching') : t('farmCenterForm.gps') }}</span>
              </AeButton>
            </div>

            <p v-if="geocodingError" class="error-message">{{ geocodingError }}</p>
            <p v-if="gpsError" class="error-message">{{ gpsError }}</p>

            <div class="map-section">
              <p class="map-hint">{{ t('farmCenterForm.mapHint') }}</p>
              <p v-if="errors.lat || errors.lon" class="error-message">
                {{ t('farmCenterForm.coordinatesRequired') }}
              </p>
              <p v-if="geocodedAddress" class="geocoded-address">{{ geocodedAddress }}</p>
              <AeMap :opt="mapOptions" :layers="mapLayers" @click="handleMapClick" />
            </div>

            <!-- Note -->
            <AeTextarea
              v-model="formData.note"
              :label="t('farmCenterForm.noteLabel')"
              :placeholder="t('farmCenterForm.notePlaceholder')"
              :rows="2"
            />
          </div>
        </template>

        <!-- TAB 2: Dati Aggiuntivi -->
        <template #extra>
          <div class="tab-content">
            <AeForm
              v-model="extraFormData"
              :dbmngSchema="extraFormSchema"
              :options="{ showActions: false, validateOnChange: false }"
            />
          </div>
        </template>

        <!-- TAB 3: Stazione Meteo -->
        <template #stazione>
          <div class="tab-content">

            <AeAlert v-if="isEditMode">
              <a :href="`#/poderi/${props.farmCenterId}/dss`">{{ t('farmCenterForm.addStationLink') }}</a>
            </AeAlert>
            <AeAlert v-else>
              {{ t('farmCenterForm.addStationLaterHint') }}
            </AeAlert>

            <p v-if="allStations.length === 0" class="map-hint">{{ t('farmCenterForm.noStationsAvailable') }}</p>
            <StationPicker
              v-else
              v-model="selectedStationId"
              :stations="allStations"
              :farm-center="formData.lat ? formData : null"
            />
          </div>
        </template>
      </AeTabPanel>

      <!-- Validation Errors -->
      <div v-if="validationErrors.length > 0" class="validation-errors">
        <p v-for="(error, index) in validationErrors" :key="index" class="error">
          {{ error }}
        </p>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePoderiStore } from '../stores/poderiStore'
import { useOrgStore } from '../stores/orgStore'
import { useDbStore } from '../stores/dbStore'
import { AeInput, AeTextarea, AeButton, AeMap, AeIcon, AeTabPanel, AeForm } from '@aevue/components'
import { baseLayers } from '../composables/useMapLayers'
import geocodingService from '../services/geocodingService'
import StationPicker from './StationPicker.vue'

const { t } = useI18n()

const props = defineProps({
  farmCenterId: {
    type: [String, Number],
    default: null
  },
  mode: {
    type: String,
    default: 'local',
    validator: (v) => ['local', 'online'].includes(v)
  }
})

const emit = defineEmits(['submit', 'cancel'])



const poderiStore = usePoderiStore()
const orgStore = useOrgStore()
const dbStore = useDbStore()
const loading = ref(false)
const geocoding = ref(false)
const geocodingError = ref(null)
const geocodedAddress = ref(null)
const gpsLoading = ref(false)
const gpsError = ref(null)
const validationErrors = ref([])

// Espone metodi e stato al componente parent (per uso con ref)
defineExpose({
  handleSubmit,
  handleCancel,
  saving: loading
})

const isEditMode = computed(() => !!props.farmCenterId)

const activeTab = ref('anagrafica')
const fcTabs = computed(() => [
  { key: 'anagrafica', label: t('farmCenterForm.tabAnagrafica') },
  { key: 'extra', label: t('farmCenterForm.tabExtra') },
  { key: 'stazione', label: t('farmCenterForm.tabStazione') }
])



const allStations = ref([])
const selectedStationId = ref(null)

const extraFormData = ref({})

const extraFormSchema = computed(() => ({
  fields: [
    {
      "field": "place_registered",
      "label": t('farmCenterForm.placeRegisteredLabel'),
      "type": "text", "nullable": 1
    },
    {
      "field": "practice",
      "label": t('farmCenterForm.practiceLabel'),
      "nullable": 1,
      "widget": "select",
      "voc_val": {
        "conventional": t('farmCenterForm.practiceConventional'),
        "transition": t('farmCenterForm.practiceTransition'),
        "mixed": t('farmCenterForm.practiceMixed'),
        "bio": t('farmCenterForm.practiceBio')
      }
    }
  ]
}));

const formData = ref({
  name: '',
  cuaa: '',
  place: '',
  lat: null,
  lon: null,
  note: ''
})

const errors = ref({
  name: '',
  place: '',
  lat: '',
  lon: ''
})

// Map always visible - default center on Italy, zoom closer when coordinates are set
const mapOptions = computed(() => ({
  center: {
    lat: formData.value.lat || 42.5,
    lon: formData.value.lon || 12.5
  },
  zoom: (formData.value.lat && formData.value.lon) ? 14 : 6,
  height: '250px',
  baseLayers: baseLayers,
  layer_selector: true,
  editable: true
}))

// Map layers - marker only when coordinates exist
const mapLayers = computed(() => {
  if (!formData.value.lat || !formData.value.lon) return []

  return [{
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [formData.value.lon, formData.value.lat]
        },
        properties: {
          name: formData.value.name || t('farmCenterForm.newFarmDefaultName'),
          place: formData.value.place || ''
        }
      }]
    },
    options: {
      popup: true,
      popupContent: `
        <div class="farm-popup">
          <h4>{{name}}</h4>
          <p>{{place}}</p>
        </div>
      `
    }
  }]
})

onMounted(async () => {
  const stationResult = await dbStore.select('station')
  if (stationResult.ok) {
    allStations.value = stationResult.data.filter(s => !s.disabled && (!s.device_type || s.device_type === 'weather'))
  }

  if (isEditMode.value) {
    loading.value = true
    try {
      let farmCenter = null
      if (props.mode === 'online') {
        // org-admin: l'azienda potrebbe non essere sincronizzata su Dexie in locale,
        // i dati arrivano dalla lista già caricata dallo store org (via API server-side)
        farmCenter = orgStore.orgFarmCenters.find(f => f.id_farm_center === props.farmCenterId)
      } else {
        await poderiStore.fetchFarmCenterById(props.farmCenterId)
        farmCenter = poderiStore.currentFarmCenter
      }

      if (farmCenter) {
        formData.value = {
          name: farmCenter.name || '',
          cuaa: farmCenter.cuaa || '',
          place: farmCenter.place || '',
          lat: farmCenter.lat,
          lon: farmCenter.lon || farmCenter.lng,
          note: farmCenter.note || ''
        }
        extraFormData.value = Object.fromEntries(
          extraFormSchema.value.fields.map(f => [f.field, farmCenter[f.field] ?? null])
        )
        selectedStationId.value = farmCenter.id_station || null
      }
    } catch (error) {
      console.error('Error loading farm center:', error)
      validationErrors.value = [t('farmCenterForm.loadErrorPrefix')]
    } finally {
      loading.value = false
    }
  }
})

async function handleGeocode() {
  if (!formData.value.place) return

  geocoding.value = true
  geocodingError.value = null
  geocodedAddress.value = null

  try {
    const result = await geocodingService.geocode(formData.value.place)
    formData.value.lat = result.lat
    formData.value.lon = result.lon
    geocodedAddress.value = result.display_name
  } catch (error) {
    console.error('Geocoding error:', error)
    geocodingError.value = error.message || t('farmCenterForm.geocodingErrorDefault')
  } finally {
    geocoding.value = false
  }
}

async function handleGps() {
  if (!navigator.geolocation) {
    gpsError.value = t('farmCenterForm.gpsNotSupported')
    return
  }
  gpsLoading.value = true
  gpsError.value = null
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      formData.value.lat = pos.coords.latitude
      formData.value.lon = pos.coords.longitude
      if (!formData.value.place?.trim()) {
        try {
          const address = await geocodingService.reverseGeocode(pos.coords.latitude, pos.coords.longitude)
          if (address) formData.value.place = address
        } catch (e) {
          // silenzioso
        }
      }
      gpsLoading.value = false
    },
    (err) => {
      gpsError.value = err.code === 1
        ? t('farmCenterForm.gpsPermissionDenied')
        : t('farmCenterForm.gpsUnavailable')
      gpsLoading.value = false
    },
    { enableHighAccuracy: true, timeout: 10000 }
  )
}

async function handleMapClick(event) {
  if (event && event.latlng) {
    formData.value.lat = event.latlng.lat
    formData.value.lon = event.latlng.lng

    if (!formData.value.place?.trim()) {
      try {
        const address = await geocodingService.reverseGeocode(event.latlng.lat, event.latlng.lng)
        if (address) formData.value.place = address
      } catch (e) {
        // silenzioso: il campo resta vuoto
      }
    }
  }
}

function validateForm() {
  validationErrors.value = []
  errors.value = { name: '', place: '', lat: '', lon: '' }

  let isValid = true

  if (!formData.value.name?.trim()) {
    errors.value.name = t('farmCenterForm.nameRequired')
    isValid = false
  }

  if (!formData.value.place?.trim()) {
    errors.value.place = t('farmCenterForm.placeRequired')
    isValid = false
  }

  if (formData.value.lat == null) {
    errors.value.lat = t('farmCenterForm.required')
    isValid = false
  } else if (formData.value.lat < -90 || formData.value.lat > 90) {
    errors.value.lat = t('farmCenterForm.latInvalid')
    isValid = false
  }

  if (formData.value.lon == null) {
    errors.value.lon = t('farmCenterForm.required')
    isValid = false
  } else if (formData.value.lon < -180 || formData.value.lon > 180) {
    errors.value.lon = t('farmCenterForm.lonInvalid')
    isValid = false
  }

  if (!isValid) {
    const msgs = []
    if (errors.value.name) msgs.push(errors.value.name)
    if (errors.value.place) msgs.push(errors.value.place)
    if (errors.value.lat || errors.value.lon) msgs.push(t('farmCenterForm.missingCoordinates'))
    validationErrors.value = msgs
    activeTab.value = 'anagrafica'
  }

  return isValid
}

async function handleSubmit() {
  if (!validateForm()) return

  loading.value = true

  try {
    const data = {
      name: formData.value.name?.trim(),
      cuaa: formData.value.cuaa?.trim() || null,
      place: formData.value.place?.trim(),
      lat: formData.value.lat,
      lon: formData.value.lon,
      note: formData.value.note?.trim() || '',
      id_station: selectedStationId.value || null,
      ...extraFormData.value
    }

    let result
    if (props.mode === 'online') {
      result = isEditMode.value
        ? await orgStore.updateOrgFarmCenter(props.farmCenterId, data)
        : await orgStore.createOrgFarmCenter(data)
    } else if (isEditMode.value) {
      result = await poderiStore.updateFarmCenter(props.farmCenterId, data)
    } else {
      result = await poderiStore.createFarmCenter(data)
    }


    if (result.ok) {
      emit('submit', result.data)
    } else {
      validationErrors.value = [result.message || t('farmCenterForm.saveError')]
    }
  } catch (error) {
    console.error('Error saving farm center:', error)
    validationErrors.value = [error.message || t('farmCenterForm.saveError')]
  } finally {
    loading.value = false
  }
}

function handleCancel() {
  emit('cancel')
}
</script>

<style scoped>
.farm-center-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.75rem;
}

.map-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.map-hint {
  font-size: 0.8rem;
  color: var(--text-secondary, #666);
  margin: 0 0 0.5rem 0;
}

.geocoded-address {
  font-size: 0.75rem;
  color: var(--color-success, #059669);
  margin: 0 0 0.5rem 0;
  font-style: italic;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  align-items: start;
}

@media (min-width: 769px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.location-group {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.5rem;
  align-items: end;
}

.geocode-btn {
  margin-bottom: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message {
  color: var(--danger-color, #e53e3e);
  font-size: 0.8rem;
  margin: 0;
}

.validation-errors {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 0.75rem;
}

.error {
  color: var(--danger-color, #e53e3e);
  font-size: 0.8rem;
  margin: 0.25rem 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
}
</style>
